import React, { useState, useEffect, useRef } from 'react';
import { ReplayMoment, TranscriptTurn } from '../types';
import { TargetedMicroDrillModal } from './TargetedMicroDrillModal';
import { useTranslation } from '../../../i18n';
import {
  speakSpeechSynthesis,
  stopAllSpeech,
  pauseSpeech,
  resumeSpeech,
  preloadVoices,
  claimPlayback,
  releasePlayback,
  isFemaleName,
} from '../../../services/voiceService';

import { getApiBaseUrl, attempts as attemptsApi } from '../../../services/api';
import {
  isGenericRecommendation,
  isCrossDomainContaminated,
  parseAndFormatRecommendation,
  synthesizePersonalizedRecommendation,
  cleanSpoken as cleanSpokenText,
} from '../utils/recommendationSynthesizer';
import { SpeechControlledAvatar, getPersonaAvatarSVG } from '../../simulation/components/SpeechControlledAvatar';

/**
 * Recordings are stored as media files and referenced by URL — a Cloudinary URL
 * when the provider is configured, otherwise a path served by the API itself
 * (`/api/v1/media/files/…`). A relative path has to be made absolute here because
 * the results page can be hosted on a different origin than the backend.
 */
function toPlayableAudioUrl(raw: unknown): string {
  const url = typeof raw === 'string' ? raw.trim() : '';
  if (!url) return '';
  if (url.startsWith('data:') || url.startsWith('blob:') || /^https?:\/\//i.test(url)) return url;
  return `${getApiBaseUrl()}${url.startsWith('/') ? '' : '/'}${url}`;
}

interface ReplayKeyMomentsCardProps {
  moments?: ReplayMoment[];
  personaName?: string;
  learnerName?: string;
  duration?: string;
  videoUrl?: string;
  personaAvatarUrl?: string;
  personaAvatarType?: string;
  avatarType?: string;
  personaRole?: string;
  personaMood?: string;
  genderPreference?: string;
  ttsVoice?: string;
  ttsProvider?: string;
  attemptId?: string;
  country?: string;
  tone?: string;
  transcript?: TranscriptTurn[];
  onRetakeSimulation?: () => void;
  simulationTitle?: string | null;
  category?: string | null;
  language?: string;
}

export const ReplayKeyMomentsCard: React.FC<ReplayKeyMomentsCardProps> = ({
  moments = [],
  personaName = 'Customer Persona',
  learnerName = 'Learner',
  duration = '00:00',
  videoUrl,
  personaAvatarUrl,
  personaAvatarType,
  avatarType,
  personaRole,
  personaMood,
  genderPreference,
  ttsVoice,
  ttsProvider,
  attemptId,
  country,
  tone,
  transcript: _transcript = [],
  onRetakeSimulation,
  simulationTitle = 'Simulation',
  category,
  language,
}) => {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackMode, setPlaybackMode] = useState<'AUDIO' | 'VIDEO'>(videoUrl ? 'VIDEO' : 'AUDIO');
  const [currentTimeSec, setCurrentTimeSec] = useState(0);

  useEffect(() => {
    if (videoUrl) {
      setPlaybackMode('VIDEO');
    }
  }, [videoUrl]);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);
  const [videoHasError, setVideoHasError] = useState<boolean>(false);
  const [activeFilter, setActiveFilter] = useState<'ALL' | 'IMPROVEMENT' | 'STRENGTH'>('ALL');
  const [selectedDrillMoment, setSelectedDrillMoment] = useState<ReplayMoment | null>(null);
  const [isDrillModalOpen, setIsDrillModalOpen] = useState(false);
  const [activeMomentId, setActiveMomentId] = useState<string | null>(null);
  const [currentSpeaker, setCurrentSpeaker] = useState<'learner' | 'persona' | 'coach' | null>(null);
  const [speakingText, setSpeakingText] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const timerRef = useRef<any>(null);
  const activeSpeechCancelRef = useRef<(() => void) | null>(null);
  const activeAudioElementRef = useRef<HTMLAudioElement | null>(null);
  const speechTimerRef = useRef<any>(null);
  const isPlaybackActiveRef = useRef<boolean>(isPlaying);
  isPlaybackActiveRef.current = isPlaying;
  const isPausedRef = useRef<boolean>(false);
  const currentStepRef = useRef<'persona' | 'learner' | 'coach' | null>(null);
  const remainingTextRef = useRef<string>('');

  // Clean Moment List — 100% dynamic from simulation attempt data and transcript
  const momentList: ReplayMoment[] = React.useMemo(() => {
    const getTurnText = (t: any) => t?.text || t?.content || t?.message || t?.spokenText || '';
    const getTurnSpeaker = (t: any) => ((t?.speaker || t?.role || '') + '').toUpperCase();
    const getTurnAudioUrl = (t: any) => toPlayableAudioUrl(t?.audioUrl || t?.audio_url || t?.recordingUrl || '');

    // 1. If explicit moments are provided by backend evaluation engine
    if (moments && moments.length > 0) {
      return moments.map((raw: any, idx: number) => {
        let userSaid = raw.userSaid || raw.said || raw.quote || raw.learnerSaid || raw.learnerQuote || raw.utterance || raw.evidenceQuote || '';
        if (userSaid === 'No utterance provided') userSaid = '';

        let matchedAudioUrl = toPlayableAudioUrl(raw.audioUrl || raw.learnerAudioUrl || raw.audio_url || '');

        // Match learner turn from transcript if quote is not explicitly provided
        if (!userSaid && _transcript && _transcript.length > 0) {
          if (typeof raw.turnIndex === 'number' && raw.turnIndex >= 0 && raw.turnIndex < _transcript.length) {
            userSaid = getTurnText(_transcript[raw.turnIndex]);
            if (!matchedAudioUrl) matchedAudioUrl = getTurnAudioUrl(_transcript[raw.turnIndex]);
          } else {
            const learnerTurn = _transcript.find(t => {
              const spk = getTurnSpeaker(t);
              return spk === 'LEARNER' || spk === 'USER';
            });
            if (learnerTurn) {
              userSaid = getTurnText(learnerTurn);
              if (!matchedAudioUrl) matchedAudioUrl = getTurnAudioUrl(learnerTurn);
            }
          }
        }

        // If audioUrl not directly on raw, try matching transcript turn
        if (!matchedAudioUrl && _transcript && _transcript.length > 0) {
          const matchingTurn = _transcript.find(t => {
            const txt = getTurnText(t);
            return txt && userSaid && (txt === userSaid || userSaid.includes(txt.slice(0, 20)));
          });
          if (matchingTurn) matchedAudioUrl = getTurnAudioUrl(matchingTurn);
        }

        // Extract or infer customer prompt from transcript
        let customerSaid = raw.customerSaid || raw.customerQuery || raw.query || raw.personaSaid || raw.personaQuote || '';
        if (!customerSaid && _transcript && _transcript.length > 0) {
          let matchIdx = -1;
          if (typeof raw.turnIndex === 'number' && raw.turnIndex >= 0 && raw.turnIndex < _transcript.length) {
            matchIdx = raw.turnIndex;
          } else if (userSaid) {
            matchIdx = _transcript.findIndex(
              (t) => getTurnText(t) === userSaid || (getTurnText(t) && userSaid.includes(getTurnText(t).slice(0, 15)))
            );
          }

          if (matchIdx >= 0) {
            // First search backward for the preceding AI turn
            for (let i = matchIdx - 1; i >= 0; i--) {
              const spk = getTurnSpeaker(_transcript[i]);
              if (spk === 'PERSONA' || spk === 'AI' || spk === 'ASSISTANT') {
                customerSaid = getTurnText(_transcript[i]);
                break;
              }
            }
            // If not found before, search forward
            if (!customerSaid) {
              for (let i = matchIdx + 1; i < _transcript.length; i++) {
                const spk = getTurnSpeaker(_transcript[i]);
                if (spk === 'PERSONA' || spk === 'AI' || spk === 'ASSISTANT') {
                  customerSaid = getTurnText(_transcript[i]);
                  break;
                }
              }
            }
          }

          // If still not found, search the entire transcript for any AI / Persona turn
          if (!customerSaid) {
            const anyAiTurn = _transcript.find(t => {
              const spk = getTurnSpeaker(t);
              return spk === 'PERSONA' || spk === 'AI' || spk === 'ASSISTANT';
            });
            if (anyAiTurn) customerSaid = getTurnText(anyAiTurn);
          }
        }

        const type = (raw.type || (raw.title?.toLowerCase().includes('strength') ? 'STRENGTH' : 'IMPROVEMENT')).toUpperCase();
        const existingSuggestion = raw.coachSuggestion || raw.trySaying || raw.recommendation;
        const competencyName = raw.competencyName || raw.title || raw.category || (type === 'STRENGTH' ? 'Effective Communication' : 'Conversational Alignment');

        let coachSuggestion = existingSuggestion || raw.whyItMatters || raw.explanation || '';
        let trySaying = raw.trySaying || null;

        const scenarioCtx = { title: simulationTitle, category: raw.category || category || '' };
        const isGeneric = isGenericRecommendation(coachSuggestion);
        const isContaminated = isCrossDomainContaminated(coachSuggestion, scenarioCtx);

        if (isGeneric || isContaminated || !trySaying || isGenericRecommendation(trySaying) || isCrossDomainContaminated(trySaying, scenarioCtx)) {
          const synthesized = synthesizePersonalizedRecommendation({
            type,
            competencyName,
            category: raw.category || category || '',
            criterionId: raw.relatedCriterionId || raw.criterionId || '',
            customerSaid,
            userSaid,
            whyItMatters: raw.whyItMatters || raw.explanation || '',
            explanation: raw.explanation || raw.whyItMatters || '',
            existingSuggestion: isContaminated ? '' : existingSuggestion,
            scenarioTitle: simulationTitle,
          });
          coachSuggestion = synthesized.fullRecommendation;
          trySaying = synthesized.trySaying;
        } else {
          const parsed = parseAndFormatRecommendation(coachSuggestion);
          coachSuggestion = parsed.fullRecommendation;
          trySaying = parsed.trySaying || trySaying;
        }

        let timeSec = typeof raw.timeSeconds === 'number' ? raw.timeSeconds : (idx + 1) * 20;
        if (typeof raw.timestamp === 'string' && raw.timestamp.includes(':')) {
          const parts = raw.timestamp.split(':');
          const pMin = parseInt(parts[0], 10);
          const pSec = parseInt(parts[1], 10);
          if (!isNaN(pMin) && !isNaN(pSec)) {
            timeSec = pMin * 60 + pSec;
          }
        }

        const minStr = String(Math.floor(timeSec / 60)).padStart(2, '0');
        const secStr = String(timeSec % 60).padStart(2, '0');
        const timestamp = `${minStr}:${secStr}`;

        return {
          id: raw.id || `moment-${idx + 1}`,
          timestamp,
          timeSeconds: timeSec,
          title: raw.title || `Key Moment #${idx + 1}`,
          competencyCode: raw.competencyCode || raw.category || 'COMMUNICATION',
          competencyName,
          type,
          customerSaid: cleanSpokenText(customerSaid),
          userSaid: userSaid || (type === 'STRENGTH' ? 'I understand and will proceed with the requested steps.' : 'Let me verify that information for you.'),
          coachSuggestion,
          recommendation: raw.recommendation && !isGenericRecommendation(raw.recommendation) ? raw.recommendation : coachSuggestion,
          trySaying,
          audioUrl: matchedAudioUrl,
        };
      });
    }

    // 2. If moments is empty but we have transcript turns, dynamically build moments from the actual dialogue
    if (_transcript && _transcript.length > 0) {
      const learnerTurns = _transcript.filter(t => {
        const spk = getTurnSpeaker(t);
        return spk === 'LEARNER' || spk === 'USER';
      });

      if (learnerTurns.length > 0) {
        return learnerTurns.slice(0, 4).map((lt, idx) => {
          const lIdx = _transcript.indexOf(lt);
          let customerSaid = '';
          if (lIdx > 0) {
            for (let i = lIdx - 1; i >= 0; i--) {
              const spk = getTurnSpeaker(_transcript[i]);
              if (spk === 'PERSONA' || spk === 'AI' || spk === 'ASSISTANT') {
                customerSaid = getTurnText(_transcript[i]);
                break;
              }
            }
          }
          if (!customerSaid) {
            for (let i = lIdx + 1; i < _transcript.length; i++) {
              const spk = getTurnSpeaker(_transcript[i]);
              if (spk === 'PERSONA' || spk === 'AI' || spk === 'ASSISTANT') {
                customerSaid = getTurnText(_transcript[i]);
                break;
              }
            }
          }

          const cleanSpoken = (str: string) => (str || '')
            .replace(/^(CUSTOMER|LEARNER|AI|USER|PERSONA)\s*(\([^)]*\))?\s*(SAID|SAYS)?\s*:\s*/i, '')
            .replace(/^\[(CUSTOMER|LEARNER|AI|USER|PERSONA|[^\]]+)\]\s*:\s*/i, '')
            .replace(/^["']|["']$/g, '')
            .trim();

          const userSaid = cleanSpoken(getTurnText(lt));
          const timeSec = typeof (lt as any).turnSec === 'number'
            ? (lt as any).turnSec
            : typeof (lt as any).elapsedSeconds === 'number'
              ? (lt as any).elapsedSeconds
              : typeof (lt as any).timestamp === 'number'
                ? (lt as any).timestamp
                : (idx + 1) * 25;
          const minStr = String(Math.floor(timeSec / 60)).padStart(2, '0');
          const secStr = String(timeSec % 60).padStart(2, '0');

          const type = idx === 0 ? 'IMPROVEMENT' : 'STRENGTH';
          const competencyName = idx === 0 ? 'Ownership & Follow-up' : 'Empathy & Validation';
          const synthesized = synthesizePersonalizedRecommendation({
            type,
            competencyName,
            category: 'COMMUNICATION',
            customerSaid,
            userSaid,
            scenarioTitle: simulationTitle,
          });

          return {
            id: `moment-dyn-${idx + 1}`,
            timestamp: `${minStr}:${secStr}`,
            timeSeconds: timeSec,
            title: idx === 0 ? 'Opportunity in Ownership & Follow-up' : `Effective Dialogue Alignment #${idx + 1}`,
            competencyCode: 'COMMUNICATION',
            competencyName,
            type,
            customerSaid: cleanSpokenText(customerSaid),
            userSaid,
            coachSuggestion: synthesized.fullRecommendation,
            recommendation: synthesized.fullRecommendation,
            trySaying: synthesized.trySaying,
            audioUrl: getTurnAudioUrl(lt),
          };
        });
      }
    }

    return [];
  }, [moments, _transcript]);

  // Compute total duration dynamically from duration prop or moment list
  const totalDurationSec = React.useMemo(() => {
    if (duration && duration.includes(':')) {
      const parts = duration.split(':');
      const min = parseInt(parts[0], 10);
      const sec = parseInt(parts[1], 10);
      if (!isNaN(min) && !isNaN(sec) && (min * 60 + sec) > 0) {
        return min * 60 + sec;
      }
    }
    if (momentList.length > 0) {
      const maxSec = Math.max(...momentList.map(m => m.timeSeconds || 0));
      return Math.max(60, maxSec + 20);
    }
    if (_transcript && _transcript.length > 0) {
      return Math.max(60, _transcript.length * 20);
    }
    return 180;
  }, [duration, momentList, _transcript]);

  const improvementCount = momentList.filter((m) => m.type === 'IMPROVEMENT' || !m.type).length;
  const strengthCount = momentList.filter((m) => m.type === 'STRENGTH').length;

  // Voice Preload on Mount
  useEffect(() => {
    preloadVoices();
    return () => {
      stopAudio();
    };
  }, []);

  const stopAudio = () => {
    if (activeAudioElementRef.current) {
      try {
        activeAudioElementRef.current.pause();
        activeAudioElementRef.current.src = '';
      } catch {}
      activeAudioElementRef.current = null;
    }
    if (activeSpeechCancelRef.current) {
      activeSpeechCancelRef.current();
      activeSpeechCancelRef.current = null;
    }
    if (speechTimerRef.current) {
      clearTimeout(speechTimerRef.current);
      speechTimerRef.current = null;
    }
    stopAllSpeech();
    releasePlayback('replay-key-moments-card');
    if (videoRef.current) {
      videoRef.current.pause();
    }
    setIsPlaying(false);
    isPausedRef.current = false;
    isPlaybackActiveRef.current = false;
    setCurrentSpeaker(null);
    setSpeakingText(null);
  };

  const pauseAudio = () => {
    if (activeAudioElementRef.current) {
      try {
        activeAudioElementRef.current.pause();
      } catch {}
    }
    pauseSpeech();
    if (videoRef.current) {
      videoRef.current.pause();
    }
    setIsPlaying(false);
    isPausedRef.current = true;
  };

  const resumeAudio = () => {
    isPausedRef.current = false;
    isPlaybackActiveRef.current = true;
    setIsPlaying(true);
    if (activeAudioElementRef.current) {
      try {
        activeAudioElementRef.current.play().catch(() => {});
      } catch {}
    }
    resumeSpeech();
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  };

  const speakUtterance = (
    text: string,
    role: 'persona' | 'learner' | 'coach',
    onComplete?: () => void,
    audioUrl?: string
  ) => {
    if (!text || !text.trim()) {
      if (onComplete) onComplete();
      return;
    }

    currentStepRef.current = role;
    remainingTextRef.current = text;
    setCurrentSpeaker(role);
    setSpeakingText(text);

    // LEARNER VOICE RULE: Only use learner's real recorded voice! Never use synthetic robotic TTS for the learner!
    if (role === 'learner') {
      if (audioUrl && audioUrl.trim()) {
        try {
          if (activeAudioElementRef.current) {
            activeAudioElementRef.current.pause();
            activeAudioElementRef.current.src = '';
          }
          const audio = new Audio(audioUrl);
          activeAudioElementRef.current = audio;
          audio.playbackRate = playbackSpeed;

          audio.onplay = () => {
            setCurrentSpeaker('learner');
            setSpeakingText(text);
          };
          audio.onended = () => {
            setCurrentSpeaker(null);
            setSpeakingText(null);
            activeAudioElementRef.current = null;
            if (onComplete) onComplete();
          };
          audio.onerror = () => {
            activeAudioElementRef.current = null;
            executeVoiceSynthesisFallback();
          };

          const playPromise = audio.play();
          if (playPromise !== undefined) {
            playPromise.catch(() => {
              activeAudioElementRef.current = null;
              executeVoiceSynthesisFallback();
            });
          }

          activeSpeechCancelRef.current = () => {
            try {
              audio.pause();
              audio.src = '';
            } catch {}
            activeAudioElementRef.current = null;
          };
          return;
        } catch (err) {
          executeVoiceSynthesisFallback();
          return;
        }
      } else {
        // Fall back to distinct learner voice synthesis matching the old system
        executeVoiceSynthesisFallback();
        return;
      }
    }

    // Persona or Coach: use recorded audio if provided, otherwise speech synthesis
    if (audioUrl && audioUrl.trim()) {
      try {
        if (activeAudioElementRef.current) {
          activeAudioElementRef.current.pause();
          activeAudioElementRef.current.src = '';
        }
        const audio = new Audio(audioUrl);
        activeAudioElementRef.current = audio;
        audio.playbackRate = playbackSpeed;
        audio.onplay = () => {
          setCurrentSpeaker(role);
          setSpeakingText(text);
        };
        audio.onended = () => {
          setCurrentSpeaker(null);
          setSpeakingText(null);
          activeAudioElementRef.current = null;
          if (onComplete) onComplete();
        };
        audio.onerror = () => {
          activeAudioElementRef.current = null;
          executeVoiceSynthesisFallback();
        };
        const playPromise = audio.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            executeVoiceSynthesisFallback();
          });
        }
        activeSpeechCancelRef.current = () => {
          try {
            audio.pause();
            audio.src = '';
          } catch {}
          activeAudioElementRef.current = null;
        };
        return;
      } catch (e) {
        // Fall back to synthesis
      }
    }

    function executeVoiceSynthesisFallback() {
      const cleanPersonaName = (personaName || '').replace(/\s*\([^)]*\)/g, '').trim() || personaName;
      const effectiveGender = role === 'persona'
        ? (genderPreference || (isFemaleName(cleanPersonaName) ? 'female' : 'male'))
        : (isFemaleName(learnerName) ? 'female' : 'male');

      // For AI persona, prefer authentic cloud TTS synthesis if attemptId is available
      if (role === 'persona' && attemptId && text && text.trim()) {
        let isCancelled = false;
        attemptsApi.synthesizeSpeech(attemptId, {
          text,
          ttsVoice: ttsVoice || (effectiveGender === 'male' ? 'onyx' : 'nova'),
          ttsProvider,
          genderPreference: effectiveGender,
          country,
          tone,
        }).then((res: any) => {
          if (isCancelled) return;
          const rawBase64 = res?.data?.audioBase64 || res?.audioBase64;
          if (res && res.success && rawBase64) {
            const mime = res.data?.mimeType || res.mimeType || 'audio/mpeg';
            const dataUrl = `data:${mime};base64,${rawBase64}`;
            try {
              if (activeAudioElementRef.current) {
                activeAudioElementRef.current.pause();
                activeAudioElementRef.current.src = '';
              }
              const audio = new Audio(dataUrl);
              activeAudioElementRef.current = audio;
              audio.playbackRate = playbackSpeed;
              audio.onplay = () => {
                if (isCancelled) return;
                setCurrentSpeaker('persona');
                setSpeakingText(text);
              };
              audio.onended = () => {
                if (isCancelled) return;
                setCurrentSpeaker(null);
                setSpeakingText(null);
                activeAudioElementRef.current = null;
                if (onComplete) onComplete();
              };
              audio.onerror = () => {
                if (isCancelled) return;
                activeAudioElementRef.current = null;
                runLocalWebSpeechFallback();
              };
              const playPromise = audio.play();
              if (playPromise !== undefined) {
                playPromise.catch(() => {
                  if (isCancelled) return;
                  activeAudioElementRef.current = null;
                  runLocalWebSpeechFallback();
                });
              }
              activeSpeechCancelRef.current = () => {
                isCancelled = true;
                try {
                  audio.pause();
                  audio.src = '';
                } catch {}
                activeAudioElementRef.current = null;
              };
              return;
            } catch (err) {
              runLocalWebSpeechFallback();
            }
          } else {
            runLocalWebSpeechFallback();
          }
        }).catch(() => {
          if (!isCancelled) {
            runLocalWebSpeechFallback();
          }
        });

        activeSpeechCancelRef.current = () => {
          isCancelled = true;
        };
        return;
      }

      runLocalWebSpeechFallback();

      function runLocalWebSpeechFallback() {
        const handle = speakSpeechSynthesis({
          text,
          role,
          personaName: cleanPersonaName,
          learnerName,
          lang: language && language.startsWith('hi') ? 'hi-IN' : language,
          genderPreference: effectiveGender,
          ttsVoice: role === 'persona' ? (ttsVoice || (effectiveGender === 'male' ? 'onyx' : 'nova')) : undefined,
          country: role === 'persona' ? country : undefined,
          tone: role === 'persona' ? tone : undefined,
          rateMultiplier: playbackSpeed,
          onStart: () => {
            setCurrentSpeaker(role);
            setSpeakingText(text);
          },
          onEnd: () => {
            setCurrentSpeaker(null);
            setSpeakingText(null);
            if (onComplete) onComplete();
          },
          onError: () => {
            setCurrentSpeaker(null);
            setSpeakingText(null);
            if (onComplete) onComplete();
          },
        });

        activeSpeechCancelRef.current = handle.cancel;
      }
    }

    executeVoiceSynthesisFallback();
  };

  const filteredMoments = momentList.filter((m) => {
    if (activeFilter === 'IMPROVEMENT') return m.type === 'IMPROVEMENT' || !m.type;
    if (activeFilter === 'STRENGTH') return m.type === 'STRENGTH';
    return true;
  });

  // Play both ends: customer query -> learner reply
  const playDialogueSequence = (moment: ReplayMoment, resumeFromCurrent: boolean = false) => {
    const startSec = resumeFromCurrent && currentTimeSec > 0 ? currentTimeSec : (moment.timeSeconds || 15);
    stopAudio();
    claimPlayback('replay-key-moments-card', () => {
      stopAudio();
    });
    isPausedRef.current = false;
    isPlaybackActiveRef.current = true;
    setActiveMomentId(moment.id || null);
    setCurrentTimeSec(startSec);
    setIsPlaying(true);

    const customerQuery = moment.customerSaid || '';
    const learnerReply = moment.userSaid || '';

    // When recorded session video is present and working:
    if (videoUrl && videoRef.current && !videoHasError) {
      videoRef.current.currentTime = startSec;
      videoRef.current.playbackRate = playbackSpeed;
      videoRef.current.play().catch(() => {});

      if (customerQuery) {
        // AI speaks first; video is muted while synthetic customer speaks to prevent overlapping audio
        if (videoRef.current) videoRef.current.muted = true;
        speakUtterance(customerQuery, 'persona', () => {
          if (!isPlaybackActiveRef.current) return;
          // Unmute video for learner turn
          if (videoRef.current) {
            videoRef.current.muted = false;
            videoRef.current.volume = 1;
          }
          if (learnerReply) {
            setCurrentSpeaker('learner');
            setSpeakingText(learnerReply);
          } else {
            setIsPlaying(false);
            isPlaybackActiveRef.current = false;
          }
        });
        return;
      } else if (learnerReply) {
        if (videoRef.current) {
          videoRef.current.muted = false;
          videoRef.current.volume = 1;
        }
        setCurrentSpeaker('learner');
        setSpeakingText(learnerReply);
        return;
      }
    }

    if (customerQuery) {
      // Step 1: Customer speaks first
      speakUtterance(customerQuery, 'persona', () => {
        if (!isPlaybackActiveRef.current) return;
        // Step 2: Learner responds with real voice only (no synthetic robotic TTS)
        if (learnerReply) {
          speechTimerRef.current = setTimeout(() => {
            if (!isPlaybackActiveRef.current) return;
            speakUtterance(learnerReply, 'learner', () => {
              setIsPlaying(false);
              isPlaybackActiveRef.current = false;
            }, moment.audioUrl);
          }, 350);
        } else {
          setIsPlaying(false);
          isPlaybackActiveRef.current = false;
        }
      });
    } else if (learnerReply) {
      speakUtterance(learnerReply, 'learner', () => {
        setIsPlaying(false);
        isPlaybackActiveRef.current = false;
      }, moment.audioUrl);
    } else {
      setIsPlaying(false);
      isPlaybackActiveRef.current = false;
    }
  };

  const handleScrubSeek = (targetSec: number) => {
    const clampedSec = Math.max(0, Math.min(totalDurationSec, targetSec));
    setCurrentTimeSec(clampedSec);
    if (videoRef.current) {
      videoRef.current.currentTime = clampedSec;
      if (isPlaying) {
        videoRef.current.play().catch(() => {});
      }
    }

    // Find the moment closest to or preceding targetSec
    let targetMoment: ReplayMoment | null = null;
    if (momentList.length > 0) {
      const pastMoments = momentList.filter((m) => (m.timeSeconds || 0) <= clampedSec);
      if (pastMoments.length > 0) {
        targetMoment = pastMoments[pastMoments.length - 1];
      } else {
        targetMoment = momentList[0];
      }
    }

    if (targetMoment) {
      setActiveMomentId(targetMoment.id || null);
      if (!videoUrl && (isPlaying || isPlaybackActiveRef.current)) {
        playDialogueSequence(targetMoment, true);
      }
    }
  };

  const togglePlayPause = () => {
    if (isPlaying) {
      pauseAudio();
    } else if (isPausedRef.current) {
      resumeAudio();
    } else {
      const activeMoment = momentList.find((m) => m.id === activeMomentId) || momentList[0];
      if (activeMoment) {
        playDialogueSequence(activeMoment, true);
      } else {
        setIsPlaying(true);
      }
    }
  };

  const handleSeekMoment = (moment: ReplayMoment) => {
    if (isPlaying && activeMomentId === moment.id) {
      pauseAudio();
    } else {
      playDialogueSequence(moment, false);
    }
  };

  const handlePlayCoachModel = (moment: ReplayMoment, e: React.MouseEvent) => {
    e.stopPropagation();
    if (isPlaying && currentSpeaker === 'coach' && activeMomentId === moment.id) {
      pauseAudio();
      return;
    }
    if (isPausedRef.current && currentSpeaker === 'coach' && activeMomentId === moment.id) {
      resumeAudio();
      return;
    }
    stopAudio(); // Pauses video so coach guidance plays cleanly without video overlap
    claimPlayback('replay-key-moments-card', () => {
      stopAudio();
    });
    isPlaybackActiveRef.current = true;
    setActiveMomentId(moment.id || null);
    setIsPlaying(true);
    const speech = moment.coachSuggestion || moment.recommendation || 'Here is the recommended response.';
    speakUtterance(speech, 'coach', () => {
      setIsPlaying(false);
      isPlaybackActiveRef.current = false;
    });
  };

  const handlePlaySingleVoice = (text: string, voiceType: 'learner' | 'persona', momentId: string, e: React.MouseEvent, audioUrl?: string) => {
    e.stopPropagation();
    if (isPlaying && currentSpeaker === voiceType && activeMomentId === momentId) {
      pauseAudio();
      return;
    }
    if (isPausedRef.current && currentSpeaker === voiceType && activeMomentId === momentId) {
      resumeAudio();
      return;
    }
    stopAudio();
    claimPlayback('replay-key-moments-card', () => {
      stopAudio();
    });
    isPlaybackActiveRef.current = true;
    setActiveMomentId(momentId);
    setIsPlaying(true);

    // If recorded video is present, seek video to moment timestamp to play authentic recorded voice!
    if (videoUrl && videoRef.current) {
      const targetMoment = momentList.find((m) => m.id === momentId);
      const seekSec = targetMoment?.timeSeconds || currentTimeSec || 0;
      videoRef.current.currentTime = seekSec;
      videoRef.current.playbackRate = playbackSpeed;
      videoRef.current.play().catch(() => {});
      setCurrentSpeaker(voiceType);
      setSpeakingText(text);
      return;
    }

    speakUtterance(text, voiceType, () => {
      setIsPlaying(false);
      isPlaybackActiveRef.current = false;
    }, audioUrl);
  };

  // Sync playback speed to video and audio elements
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = playbackSpeed;
    }
    if (activeAudioElementRef.current) {
      activeAudioElementRef.current.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed]);

  // Scrubber timer tick (only when not playing native video to prevent jitter)
  useEffect(() => {
    if (isPlaying && !videoUrl) {
      timerRef.current = setInterval(() => {
        setCurrentTimeSec((prev) => {
          if (prev >= totalDurationSec) {
            setIsPlaying(false);
            isPlaybackActiveRef.current = false;
            return totalDurationSec;
          }
          return prev + 1;
        });
      }, 1000 / playbackSpeed);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPlaying, playbackSpeed, totalDurationSec, videoUrl]);

  const formatTime = (sec: number) => {
    const mins = Math.floor(sec / 60);
    const s = sec % 60;
    return `${mins < 10 ? '0' : ''}${mins}:${s < 10 ? '0' : ''}${s}`;
  };

  return (
    <div
      className="scorecard-card replay-card-container"
      style={{
        background: 'var(--white)',
        border: '1px solid var(--line)',
        borderRadius: '12px',
        padding: 'clamp(14px, 3vw, 24px)',
        display: 'grid',
        gap: '20px',
        minWidth: 0,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      {/* Header */}
      <div className="replay-card-header">
        <div style={{ minWidth: 0, flex: '1 1 240px', maxWidth: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '18px', flexShrink: 0 }}>⏯️</span>
            <h2 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--navy)', margin: 0, minWidth: 0, wordBreak: 'break-word' }}>
              {t('scorecard.replay_moments_title', undefined, 'Replay & Key Moments')}
            </h2>
          </div>
          <p style={{ fontSize: '13px', color: 'var(--muted)', margin: 0, lineHeight: 1.45, wordBreak: 'break-word' }}>
            {t('scorecard.replay_moments_subtitle', undefined, 'Scrub simulation timeline to hear actual dialogue exchanges and coaching recommendations.')}
          </p>
        </div>

        {/* Mode Switcher: Video vs Audio Player */}
        <div className="replay-mode-switcher">
          <button
            type="button"
            onClick={() => setPlaybackMode('AUDIO')}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 600,
              border: 'none',
              cursor: 'pointer',
              background: playbackMode === 'AUDIO' ? '#FFFFFF' : 'transparent',
              color: playbackMode === 'AUDIO' ? '#0F172A' : '#64748B',
              boxShadow: playbackMode === 'AUDIO' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              minHeight: '34px',
              boxSizing: 'border-box',
            }}
          >
            <span>🎙️</span> {t('scorecard.waveform_mode', undefined, 'Waveform')}
          </button>
          <button
            type="button"
            onClick={() => setPlaybackMode('VIDEO')}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 600,
              border: 'none',
              cursor: 'pointer',
              background: playbackMode === 'VIDEO' ? '#FFFFFF' : 'transparent',
              color: playbackMode === 'VIDEO' ? '#0F172A' : '#64748B',
              boxShadow: playbackMode === 'VIDEO' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              minHeight: '34px',
              boxSizing: 'border-box',
            }}
          >
            <span>📹</span> {t('scorecard.video_player_mode', undefined, 'Video Stage')}
          </button>
          {videoUrl && (
            <a
              href={videoUrl}
              download="simulation_recording.webm"
              target="_blank"
              rel="noopener noreferrer"
              className="replay-download-link"
              style={{
                padding: '5px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 600,
                textDecoration: 'none',
                background: '#EFF6FF',
                color: '#1D4ED8',
                border: '1px solid #BFDBFE',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                cursor: 'pointer',
                minHeight: '34px',
                boxSizing: 'border-box',
              }}
              title="Download Session Recording Video"
            >
              <span>⬇️</span> Download Recording
            </a>
          )}
        </div>
      </div>

      {/* Main Interactive Stage */}
      {playbackMode === 'VIDEO' ? (
        /* Video Simulation Dual-Feed Player */
        <div className="replay-stage-wrapper">
          {/* Dual Feed Video Stage */}
          {videoUrl && !videoHasError ? (
            <div className="replay-raw-video-box">
              <video
                ref={videoRef}
                src={videoUrl}
                controls={false}
                playsInline
                onError={(err) => {
                  console.warn('[ReplayKeyMomentsCard] Video stream failed or inaccessible, switching to interactive avatar view:', err);
                  setVideoHasError(true);
                  setPlaybackMode('AUDIO');
                }}
                onTimeUpdate={() => {
                  if (videoRef.current) {
                    setCurrentTimeSec(Math.round(videoRef.current.currentTime));
                  }
                }}
                onEnded={() => {
                  setIsPlaying(false);
                  isPlaybackActiveRef.current = false;
                }}
                style={{ width: '100%', height: '100%', objectFit: 'contain', maxWidth: '100%' }}
              />
              {/* Video Top Overlay Bar (badge & download) */}
              <div className="replay-video-top-bar">
                <div
                  style={{
                    pointerEvents: 'auto',
                    background: 'rgba(15, 23, 42, 0.75)',
                    backdropFilter: 'blur(6px)',
                    padding: '4px 10px',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#FFFFFF',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    boxSizing: 'border-box',
                  }}
                >
                  <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#EF4444', animation: isPlaying ? 'pulse 1s infinite' : 'none', flexShrink: 0 }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    REPLAY VIDEO STREAM · {personaName} & {learnerName}
                  </span>
                </div>

                <div style={{ pointerEvents: 'auto' }}>
                  <a
                    href={videoUrl}
                    download="simulation_recording.webm"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      background: 'rgba(37, 99, 235, 0.85)',
                      backdropFilter: 'blur(6px)',
                      padding: '4px 12px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: 700,
                      color: '#FFFFFF',
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      border: '1px solid rgba(255, 255, 255, 0.25)',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                    title="Download Raw Recording (WebM)"
                  >
                    <span>⬇️</span> Download Recording
                  </a>
                </div>
              </div>
            </div>
          ) : (
            <div className="replay-dual-feed-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px' }}>
              {/* Left Screen: AI Persona Customer Feed */}
              <div
                className="replay-feed-tile"
                style={{
                  aspectRatio: '16/9',
                  border: currentSpeaker === 'persona' ? '2.5px solid #F59E0B' : '2px solid rgba(255, 255, 255, 0.08)',
                  boxShadow: currentSpeaker === 'persona' ? '0 0 20px rgba(245, 158, 11, 0.25)' : 'none',
                }}
              >
                {/* Full Persona Avatar / Video taking full tile area matching live simulation */}
                <div style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'hidden' }}>
                  <SpeechControlledAvatar
                    avatarUrl={personaAvatarUrl}
                    avatarType={personaAvatarType || avatarType}
                    isSpeaking={currentSpeaker === 'persona' && isPlaying}
                    personaName={personaName}
                    genderPreference={genderPreference}
                    renderFallback={() => (
                      <div
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
                        dangerouslySetInnerHTML={{ __html: getPersonaAvatarSVG(personaName) }}
                      />
                    )}
                  />
                </div>

                {/* Top Badge: Customer Persona */}
                <div className="replay-panel-badge-top" style={{ color: '#F59E0B' }}>
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#F59E0B', animation: currentSpeaker === 'persona' ? 'pulse 1s infinite' : 'none', flexShrink: 0 }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    AI CUSTOMER · {personaName}
                  </span>
                </div>

                {/* Top Right: Live Audio Equalizer on Video Feed */}
                <div
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                    background: 'rgba(5, 15, 38, 0.85)',
                    backdropFilter: 'blur(6px)',
                    padding: '4px 6px',
                    borderRadius: '4px',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    zIndex: 10,
                  }}
                >
                  {[8, 14, 20, 12, 16].map((h, i) => (
                    <div
                      key={i}
                      style={{
                        width: '2.5px',
                        height: currentSpeaker === 'persona' && isPlaying ? `${Math.max(4, Math.round(h * (Math.sin(currentTimeSec * 4 + i) + 1.2)))}px` : '4px',
                        background: currentSpeaker === 'persona' ? '#F59E0B' : '#64748B',
                        borderRadius: '1px',
                        transition: 'height 0.1s ease',
                      }}
                    />
                  ))}
                </div>

                {/* Bottom Overlay Label */}
                <div
                  className="replay-panel-badge-bottom"
                  style={{
                    color: currentSpeaker === 'persona' ? '#FDE68A' : '#94A3B8',
                    fontWeight: currentSpeaker === 'persona' ? 700 : 500,
                  }}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {currentSpeaker === 'persona' ? '🗣️ Speaking now…' : 'Active Customer Audio Channel'}
                  </span>
                  {personaRole && <span style={{ opacity: 0.7, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>· {personaRole}</span>}
                  {personaMood && personaMood !== 'Neutral' && (
                    <span style={{ fontSize: '10px', background: 'rgba(255, 255, 255, 0.12)', padding: '1px 5px', borderRadius: '4px', color: '#FDE047', flexShrink: 0 }}>
                      {personaMood}
                    </span>
                  )}
                </div>
              </div>

              {/* Right Screen: Learner Video / Telemetry Feed */}
              <div
                className="replay-feed-tile"
                style={{
                  border: currentSpeaker === 'learner' ? '2.5px solid #38BDF8' : '2px solid rgba(255, 255, 255, 0.08)',
                  boxShadow: currentSpeaker === 'learner' ? '0 0 20px rgba(56, 189, 248, 0.25)' : 'none',
                }}
              >
                {/* Top Badge: Learner Feed */}
                <div className="replay-panel-badge-top" style={{ color: '#38BDF8' }}>
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#38BDF8', animation: currentSpeaker === 'learner' ? 'pulse 1s infinite' : 'none', flexShrink: 0 }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    LEARNER WEBCAM · {learnerName}
                  </span>
                </div>

                {/* Top Right: Live Audio Equalizer on Video Feed */}
                <div
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                    background: 'rgba(5, 15, 38, 0.85)',
                    backdropFilter: 'blur(6px)',
                    padding: '4px 6px',
                    borderRadius: '4px',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    zIndex: 10,
                  }}
                >
                  {[8, 14, 20, 12, 16].map((h, i) => (
                    <div
                      key={i}
                      style={{
                        width: '2.5px',
                        height: currentSpeaker === 'learner' && isPlaying ? `${Math.max(4, Math.round(h * (Math.cos(currentTimeSec * 4 + i) + 1.2)))}px` : '4px',
                        background: currentSpeaker === 'learner' ? '#38BDF8' : '#64748B',
                        borderRadius: '1px',
                        transition: 'height 0.1s ease',
                      }}
                    />
                  ))}
                </div>

                {/* Center Learner Avatar Chip */}
                <div
                  style={{
                    width: 'clamp(56px, 12vw, 72px)',
                    height: 'clamp(56px, 12vw, 72px)',
                    borderRadius: '50%',
                    background: '#0284C7',
                    border: currentSpeaker === 'learner' ? '3px solid #38BDF8' : '2px solid #0369A1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: currentSpeaker === 'learner' ? '0 0 20px rgba(56, 189, 248, 0.5)' : 'none',
                    transition: 'all 0.2s ease',
                    fontSize: 'clamp(20px, 4vw, 26px)',
                    color: '#FFFFFF',
                    fontWeight: 700,
                    transform: currentSpeaker === 'learner' && isPlaying ? 'scale(1.04)' : 'scale(1)',
                  }}
                >
                  {learnerName ? learnerName.charAt(0).toUpperCase() : 'L'}
                </div>

                {/* Bottom Overlay Label */}
                <div
                  className="replay-panel-badge-bottom"
                  style={{
                    color: currentSpeaker === 'learner' ? '#BAE6FD' : '#94A3B8',
                    fontWeight: currentSpeaker === 'learner' ? 700 : 500,
                  }}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {currentSpeaker === 'learner' ? '🎙️ Real Learner Utterance' : 'Camera Feed Active'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Subtitle / Speaking Dialogue Ticker */}
          {speakingText && (
            <div
              className="replay-subtitle-ticker"
              style={{
                padding: '10px clamp(10px, 2.5vw, 18px)',
                background: 'rgba(15, 23, 42, 0.98)',
                borderTop: '1px solid #1E293B',
                fontSize: '12.5px',
                color: currentSpeaker === 'persona' ? '#FDE68A' : currentSpeaker === 'coach' ? '#86EFAC' : '#BAE6FD',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                lineHeight: 1.4,
                minWidth: 0,
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                flexWrap: 'wrap',
              }}
            >
              <span
                style={{
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  fontSize: '10.5px',
                  background: currentSpeaker === 'persona' ? 'rgba(245, 158, 11, 0.2)' : currentSpeaker === 'coach' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(56, 189, 248, 0.2)',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  letterSpacing: '0.04em',
                  flexShrink: 0,
                }}
              >
                {currentSpeaker === 'persona' ? `[${personaName}]` : currentSpeaker === 'coach' ? '[AI Coach]' : `[${learnerName}]`}
              </span>
              <span style={{ fontStyle: 'italic', flex: '1 1 180px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                "{speakingText}"
              </span>
            </div>
          )}

          {/* Video Control Bar & Scrubber */}
          <div className="replay-video-controls-bar">
            <button
              type="button"
              className="replay-ctrl-play"
              onClick={togglePlayPause}
              title={isPlaying ? 'Pause replay' : 'Play replay'}
              style={{
                width: '36px',
                height: '36px',
                minWidth: '36px',
                minHeight: '36px',
                borderRadius: '50%',
                background: '#FFFFFF',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0F172A',
                fontWeight: 700,
                flexShrink: 0,
                boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
              }}
            >
              {isPlaying ? '⏸' : '▶'}
            </button>
            <span className="replay-ctrl-time" style={{ fontSize: '11px', color: '#94A3B8', fontFamily: 'monospace', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {formatTime(currentTimeSec)} / {duration}
            </span>
            <div
              className="replay-ctrl-scrubber"
              style={{
                flex: 1,
                minWidth: '60px',
                height: '10px',
                padding: '4px 0',
                background: 'transparent',
                cursor: 'pointer',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
              }}
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const clickX = e.clientX - rect.left;
                const ratio = Math.max(0, Math.min(1, clickX / rect.width));
                const targetSec = Math.round(ratio * totalDurationSec);
                handleScrubSeek(targetSec);
              }}
            >
              <div style={{ width: '100%', height: '8px', background: '#334155', borderRadius: '4px', position: 'relative', overflow: 'visible' }}>
                <div style={{ width: `${Math.min(100, Math.max(0, (currentTimeSec / totalDurationSec) * 100))}%`, height: '100%', background: '#38BDF8', borderRadius: '4px' }} />
                {/* Moment bookmarks on video timeline */}
                {momentList.map((m) => (
                  <div
                    key={m.id}
                    title={`Jump to ${m.timestamp}: ${m.title}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSeekMoment(m);
                    }}
                    style={{
                      position: 'absolute',
                      left: `${Math.min(98, Math.max(2, ((m.timeSeconds || 15) / totalDurationSec) * 100))}%`,
                      top: '-2px',
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      background: m.type === 'STRENGTH' ? '#10B981' : '#F59E0B',
                      border: '1.5px solid white',
                      cursor: 'pointer',
                      zIndex: 2,
                      transform: 'translateX(-50%)',
                    }}
                  />
                ))}
              </div>
            </div>
            <button
              type="button"
              className="replay-ctrl-speed"
              onClick={() => {
                const newSpeed = playbackSpeed === 1 ? 1.25 : playbackSpeed === 1.25 ? 1.5 : 1;
                setPlaybackSpeed(newSpeed);
                if (videoRef.current) videoRef.current.playbackRate = newSpeed;
              }}
              style={{
                background: '#1E293B',
                border: 'none',
                color: '#94A3B8',
                fontSize: '11px',
                fontWeight: 700,
                padding: '4px 10px',
                borderRadius: '5px',
                cursor: 'pointer',
                flexShrink: 0,
                minHeight: '28px',
                whiteSpace: 'nowrap',
              }}
            >
              {playbackSpeed}x
            </button>
          </div>
        </div>
      ) : (
        /* Dark Audio Wave Scrubber Player */
        <div className="replay-audio-stage">
          {/* Top Speaker Row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12px', fontWeight: 600, flexWrap: 'wrap', gap: '8px', minWidth: 0, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
            <span style={{ color: currentSpeaker === 'persona' ? '#F59E0B' : '#94A3B8', display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#F59E0B' }} />
              AI customer · {personaName}
            </span>
            {speakingText && (
              <span style={{ fontSize: '11px', color: '#38BDF8', fontStyle: 'italic', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                🗣️ "{speakingText}"
              </span>
            )}
            <span style={{ color: currentSpeaker === 'learner' ? '#38BDF8' : '#94A3B8', display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
              You · {learnerName}
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#38BDF8' }} />
            </span>
          </div>

          {/* Center Waveforms & Play/Pause */}
          <div className="replay-audio-wave-center">
            {/* Left Audio Wave Bars (Customer Voice Activity) */}
            <div className="replay-wave-bars-group" style={{ justifyContent: 'flex-end' }}>
              {[12, 18, 28, 14, 22, 34, 16, 26, 10, 20, 30, 18, 24, 12, 16].map((h, i) => {
                const isWaveActive = isPlaying && (currentSpeaker === 'persona' || currentSpeaker === null);
                const dynamicHeight = isWaveActive ? Math.max(6, Math.round(h * (Math.sin(currentTimeSec * 3 + i) + 1.2))) : h;
                const isOuter = i < 4 || i > 11;
                return (
                  <div
                    key={i}
                    className={isOuter ? 'replay-wave-outer-bar' : ''}
                    style={{
                      width: '3px',
                      height: `${dynamicHeight}px`,
                      background: isWaveActive ? '#F59E0B' : '#475569',
                      borderRadius: '2px',
                      transition: 'height 0.12s ease',
                      flexShrink: 0,
                    }}
                  />
                );
              })}
            </div>

            {/* Big Center Play Button */}
            <button
              type="button"
              onClick={togglePlayPause}
              title={isPlaying ? 'Pause dialogue audio' : 'Play dialogue audio'}
              style={{
                width: '46px',
                height: '46px',
                minWidth: '46px',
                minHeight: '46px',
                borderRadius: '50%',
                background: '#FFFFFF',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: '#0F172A',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                flexShrink: 0,
                transition: 'transform 0.1s ease',
              }}
            >
              {isPlaying ? (
                <svg style={{ width: '18px', height: '18px' }} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                </svg>
              ) : (
                <svg style={{ width: '18px', height: '18px', marginLeft: '2px' }} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>

            {/* Right Audio Wave Bars (Learner Voice Activity) */}
            <div className="replay-wave-bars-group" style={{ justifyContent: 'flex-start' }}>
              {[14, 22, 12, 30, 20, 16, 32, 24, 18, 28, 14, 22, 10, 18, 12].map((h, i) => {
                const isWaveActive = isPlaying && (currentSpeaker === 'learner' || currentSpeaker === null);
                const dynamicHeight = isWaveActive ? Math.max(6, Math.round(h * (Math.cos(currentTimeSec * 3 + i) + 1.2))) : h;
                const isOuter = i < 4 || i > 11;
                return (
                  <div
                    key={i}
                    className={isOuter ? 'replay-wave-outer-bar' : ''}
                    style={{
                      width: '3px',
                      height: `${dynamicHeight}px`,
                      background: isWaveActive ? '#38BDF8' : '#475569',
                      borderRadius: '2px',
                      transition: 'height 0.12s ease',
                      flexShrink: 0,
                    }}
                  />
                );
              })}
            </div>
          </div>

          {/* Bottom Timeline Scrubber */}
          <div className="replay-audio-scrubber-bar">
            <span className="replay-audio-curtime" style={{ fontFamily: 'monospace', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {formatTime(currentTimeSec)}
            </span>
            <div
              className="replay-audio-track"
              style={{
                flex: 1,
                minWidth: '60px',
                position: 'relative',
                height: '10px',
                padding: '4px 0',
                background: 'transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const clickX = e.clientX - rect.left;
                const ratio = Math.max(0, Math.min(1, clickX / rect.width));
                const targetSec = Math.round(ratio * totalDurationSec);
                handleScrubSeek(targetSec);
              }}
            >
              <div style={{ width: '100%', height: '8px', background: '#334155', borderRadius: '4px', position: 'relative', overflow: 'visible' }}>
                <div style={{ position: 'absolute', left: 0, width: `${Math.min(100, Math.max(0, (currentTimeSec / totalDurationSec) * 100))}%`, height: '100%', background: '#38BDF8', borderRadius: '4px' }} />
                {/* Key moment pin markers on timeline */}
                {momentList.map((m) => (
                  <div
                    key={m.id}
                    title={`Jump to ${m.timestamp}: ${m.title}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSeekMoment(m);
                    }}
                    style={{
                      position: 'absolute',
                      left: `${Math.min(98, Math.max(2, ((m.timeSeconds || 15) / totalDurationSec) * 100))}%`,
                      top: '-2px',
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      background: m.type === 'STRENGTH' ? '#10B981' : '#F59E0B',
                      border: '1.5px solid white',
                      cursor: 'pointer',
                      zIndex: 2,
                      transform: 'translateX(-50%)',
                    }}
                  />
                ))}
              </div>
            </div>
            <div className="replay-audio-duration-speed">
              <span style={{ fontFamily: 'monospace', whiteSpace: 'nowrap', flexShrink: 0 }}>{duration}</span>
              <button
                type="button"
                onClick={() => setPlaybackSpeed((s) => (s === 1 ? 1.25 : s === 1.25 ? 1.5 : 1))}
                style={{
                  background: '#1E293B',
                  border: 'none',
                  color: '#94A3B8',
                  fontSize: '10.5px',
                  fontWeight: 700,
                  padding: '3px 8px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  flexShrink: 0,
                  minHeight: '26px',
                  whiteSpace: 'nowrap',
                }}
              >
                {playbackSpeed}x
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="replay-filters-container">
        <button
          type="button"
          onClick={() => setActiveFilter('ALL')}
          className="replay-filter-pill"
          style={{
            fontWeight: 700,
            border: activeFilter === 'ALL' ? '1px solid var(--blue)' : '1px solid var(--line)',
            background: activeFilter === 'ALL' ? 'var(--blue-soft)' : 'var(--white)',
            color: activeFilter === 'ALL' ? 'var(--blue)' : 'var(--navy)',
          }}
        >
          {t('scorecard.filter_all', { count: momentList.length }, `All moments (${momentList.length})`)}
        </button>

        <button
          type="button"
          onClick={() => setActiveFilter('IMPROVEMENT')}
          className="replay-filter-pill"
          style={{
            fontWeight: 600,
            border: activeFilter === 'IMPROVEMENT' ? '1px solid var(--blue)' : '1px solid var(--line)',
            background: activeFilter === 'IMPROVEMENT' ? 'var(--blue-soft)' : 'var(--white)',
            color: activeFilter === 'IMPROVEMENT' ? 'var(--blue)' : 'var(--muted)',
          }}
        >
          {t('scorecard.filter_improvement', undefined, 'Scope for improvement')} ({improvementCount})
        </button>

        <button
          type="button"
          onClick={() => setActiveFilter('STRENGTH')}
          className="replay-filter-pill"
          style={{
            fontWeight: 600,
            border: activeFilter === 'STRENGTH' ? '1px solid var(--blue)' : '1px solid var(--line)',
            background: activeFilter === 'STRENGTH' ? 'var(--blue-soft)' : 'var(--white)',
            color: activeFilter === 'STRENGTH' ? 'var(--blue)' : 'var(--muted)',
          }}
        >
          {t('scorecard.filter_strength', undefined, 'Strengths')} ({strengthCount})
        </button>
      </div>

      {/* Moment Cards */}
      <div className="replay-moments-list">
        {filteredMoments.map((moment, idx) => {
          const isSelected = activeMomentId === moment.id;
          const isStrength = moment.type === 'STRENGTH';

          return (
            <div
              key={moment.id || idx}
              onClick={() => handleSeekMoment(moment)}
              className="replay-moment-card"
              style={{
                border: isSelected ? '1.5px solid var(--blue)' : '1px solid var(--line)',
                background: isSelected ? 'var(--blue-soft, #eff6ff)' : 'var(--white)',
              }}
            >
              {/* Moment Header */}
              <div className="replay-moment-header-row">
                <div className="replay-moment-title-group">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSeekMoment(moment);
                    }}
                    style={{
                      border: 'none',
                      background: isSelected ? 'var(--blue)' : '#eff6ff',
                      color: isSelected ? '#FFFFFF' : 'var(--blue)',
                      fontSize: '11px',
                      fontWeight: 800,
                      padding: '4px 8px',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      flexShrink: 0,
                      minHeight: '28px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    ▶ {moment.timestamp}
                  </button>
                  <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--navy)', minWidth: 0, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                    {moment.title}
                  </span>
                </div>

                <span
                  style={{
                    fontSize: '10.5px',
                    fontWeight: 700,
                    background: isStrength ? '#ECFDF5' : '#FEF3C7',
                    color: isStrength ? '#065F46' : '#B45309',
                    padding: '3px 8px',
                    borderRadius: '4px',
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {isStrength ? t('scorecard.filter_strength', undefined, 'Strength') : t('scorecard.filter_improvement', undefined, 'Improve')} · {moment.competencyName || 'Objection Handling'}
                </span>
              </div>

              {/* Customer context if present */}
              {moment.customerSaid && (
                <div
                  style={{
                    padding: '10px 12px',
                    borderRadius: '6px',
                    background: '#FEF3C7',
                    border: '1px solid #FDE68A',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '10px',
                    flexWrap: 'wrap',
                    minWidth: 0,
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                  }}
                >
                  <div style={{ minWidth: 0, flex: '1 1 180px', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                    <strong style={{ color: '#92400E', textTransform: 'uppercase', fontSize: '10px', display: 'block', marginBottom: '2px' }}>
                      {t('scorecard.ai_customer', undefined, 'Customer')} ({personaName}) {t('scorecard.what_you_said', undefined, 'said')}:
                    </strong>
                    <span style={{ color: '#78350F', fontStyle: 'italic' }}>
                      "{moment.customerSaid}"
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => handlePlaySingleVoice(moment.customerSaid || '', 'persona', moment.id || '', e)}
                    style={{
                      border: 'none',
                      background: isPlaying && currentSpeaker === 'persona' && activeMomentId === moment.id ? '#F59E0B' : '#FDE68A',
                      color: isPlaying && currentSpeaker === 'persona' && activeMomentId === moment.id ? '#FFFFFF' : '#92400E',
                      fontSize: '10.5px',
                      fontWeight: 700,
                      padding: '4px 8px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      flexShrink: 0,
                      minHeight: '28px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {isPlaying && currentSpeaker === 'persona' && activeMomentId === moment.id ? `⏸ ${t('scorecard.pause_moment', undefined, 'Pause')}` : `🔊 ${t('scorecard.play_moment', undefined, 'Play Customer')}`}
                  </button>
                </div>
              )}

              {/* Comparison Side-by-Side: You Said vs Try Saying */}
              <div className="moment-comparison-grid">
                {/* Left: You Said */}
                <div
                  style={{
                    padding: '12px 14px',
                    borderRadius: '8px',
                    background: '#FFFBEB',
                    border: '1px solid #FDE68A',
                    display: 'grid',
                    gap: '6px',
                    minWidth: 0,
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '6px', minWidth: 0 }}>
                    <span style={{ fontSize: '9.5px', fontWeight: 800, textTransform: 'uppercase', color: '#92400E', letterSpacing: '.04em' }}>
                      {t('scorecard.what_you_said', undefined, 'YOU SAID')} {moment.audioUrl ? '🎙️' : ''}
                    </span>
                    <button
                      type="button"
                      onClick={(e) => handlePlaySingleVoice(moment.userSaid || '', 'learner', moment.id || '', e, moment.audioUrl)}
                      style={{
                        border: 'none',
                        background: isPlaying && currentSpeaker === 'learner' && activeMomentId === moment.id ? '#38BDF8' : 'transparent',
                        color: isPlaying && currentSpeaker === 'learner' && activeMomentId === moment.id ? '#0F172A' : '#92400E',
                        fontSize: '11px',
                        cursor: 'pointer',
                        fontWeight: 600,
                        padding: '3px 6px',
                        borderRadius: '4px',
                        flexShrink: 0,
                        minHeight: '28px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {isPlaying && currentSpeaker === 'learner' && activeMomentId === moment.id ? `⏸ ${t('scorecard.pause_moment', undefined, 'Pause')}` : `🔊 ${t('scorecard.play_moment', undefined, 'Play')}`}
                    </button>
                  </div>
                  <p style={{ margin: 0, fontSize: '12.5px', color: 'var(--navy)', fontStyle: 'italic', lineHeight: 1.45, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                    "{moment.userSaid}"
                  </p>
                </div>

                {/* Right: Try Saying / Recommendation */}
                <div
                  style={{
                    padding: '12px 14px',
                    borderRadius: '8px',
                    background: '#F0FDF4',
                    border: '1px solid #BBF7D0',
                    display: 'grid',
                    gap: '6px',
                    minWidth: 0,
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '6px', minWidth: 0 }}>
                    <span style={{ fontSize: '9.5px', fontWeight: 800, textTransform: 'uppercase', color: '#166534', letterSpacing: '.04em' }}>
                      {t('scorecard.coach_recommendation', undefined, 'TRY SAYING / RECOMMENDATION')}
                    </span>
                    <div className="replay-moment-actions-group">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedDrillMoment(moment);
                          setIsDrillModalOpen(true);
                        }}
                        style={{
                          border: 'none',
                          background: 'linear-gradient(135deg, #2563EB, #1D4ED8)',
                          color: '#FFFFFF',
                          fontSize: '11px',
                          cursor: 'pointer',
                          fontWeight: 700,
                          padding: '4px 9px',
                          borderRadius: '4px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          boxShadow: '0 1px 3px rgba(37, 99, 235, 0.3)',
                          flexShrink: 0,
                          minHeight: '28px',
                          whiteSpace: 'nowrap',
                        }}
                        title="Rehearse this objection in a quick 2-turn micro-drill"
                      >
                        🎯 {t('scorecard.targeted_drill', undefined, 'Practice (Micro-Drill)')}
                      </button>
                      <button
                        type="button"
                        onClick={(e) => handlePlayCoachModel(moment, e)}
                        style={{
                          border: 'none',
                          background: isPlaying && currentSpeaker === 'coach' && activeMomentId === moment.id ? '#166534' : '#DCFCE7',
                          color: isPlaying && currentSpeaker === 'coach' && activeMomentId === moment.id ? '#FFFFFF' : '#166534',
                          fontSize: '11px',
                          cursor: 'pointer',
                          fontWeight: 700,
                          padding: '4px 8px',
                          borderRadius: '4px',
                          flexShrink: 0,
                          minHeight: '28px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {isPlaying && currentSpeaker === 'coach' && activeMomentId === moment.id ? `⏸ ${t('scorecard.pause_moment', undefined, 'Pause Demo')}` : `🔊 ${t('scorecard.coach_recommendation', undefined, 'AI Coach Demo')}`}
                      </button>
                    </div>
                  </div>
                  {(() => {
                    const parsed = parseAndFormatRecommendation(moment.coachSuggestion || moment.recommendation || '');
                    const displayScript = moment.trySaying || parsed.trySaying;
                    const displayAdvice = parsed.advice || moment.whyItMatters;

                    if (displayScript) {
                      return (
                        <div style={{ display: 'grid', gap: '5px' }}>
                          <div style={{ fontSize: '12.5px', color: '#166534', lineHeight: 1.45, fontWeight: 600 }}>
                            <span style={{ fontWeight: 700, color: '#14532d' }}>Try saying:</span> "{displayScript}"
                          </div>
                          {displayAdvice && (
                            <p style={{ margin: 0, fontSize: '12px', color: '#15803d', lineHeight: 1.4 }}>
                              💡 {displayAdvice}
                            </p>
                          )}
                        </div>
                      );
                    }

                    return (
                      <p style={{ margin: 0, fontSize: '12.5px', color: '#166534', lineHeight: 1.45, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                        "{moment.coachSuggestion || moment.recommendation}"
                      </p>
                    );
                  })()}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    
      {/* Targeted Micro-Drill Modal */}
      <TargetedMicroDrillModal
        isOpen={isDrillModalOpen}
        onClose={() => setIsDrillModalOpen(false)}
        moment={selectedDrillMoment}
        personaName={personaName}
        personaAvatarUrl={personaAvatarUrl}
        simulationTitle={simulationTitle}
        language={language}
        onRetakeFullSimulation={onRetakeSimulation}
      />
    </div>
  );
};